/**
 * RecordSurfaceContext — resolves ONE record for a surface and publishes the
 * result as a single state machine.
 *
 * Every host that shows a record — the portaled slideout, the inline
 * split-view pane, the builder panel — mounts this once, above its chrome,
 * and reads `useRecordSurface().state` from each region:
 *
 *   loading  — nothing to paint yet (cold open with no store entry)
 *   error    — the fetch failed for a reason other than access
 *   denied   — 403, or a 404 that is not a pending optimistic creation
 *   ready    — a record to render: the store entry (summary or complete),
 *              with any preview projection applied
 *
 * Header, body, and footer each `switch` on it exhaustively, so a new state
 * is a compile error in every region instead of a silent fall-through in one
 * — and, crucially, none of them can remove the chrome's exits, because the
 * chrome is not inside this switch (docs/claude/REACT_19_PATTERNS.md § 1.2).
 *
 * Owns, besides resolution: the unsaved-changes guard (surfaced through
 * `onGuardChange` so hosts can intercept their own dismissal paths), the
 * save-bar state, live-subscription upkeep for restricted types, and the
 * surface's observability scope. When `ready`, it also provides
 * `RecordDraftProvider` + `RecordViewProvider` so regions can render.
 *
 * ## Drafts
 *
 * A pending proposal reaches the surface as a preview overlay
 * (`useOverlaySubscription`): the record is projected one step ahead, and
 * `RecordDraftProvider` carries the prior values so changed rows read
 * `old → new`. Two save-bar producers feed the one floating `SaveBar`:
 * inline edits (`useOverviewEditState`) on a live record, and — when a host
 * passes `draftCommit` — the proposal itself (`useDraftSaveBarState`), whose
 * Save approves it. A host that only SHOWS a draft (the action center, where
 * approval lives in the proposal panel) omits `draftCommit`; a host that
 * REVIEWS one (the builder) passes it.
 */

import { type ReactNode, useEffect, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import type { UnsavedChangesModalProps } from '@/hooks/useUnsavedChangesGuard';
import { projectOverlay } from '@/lib/crm/preview';
import { useRestrictedSubscription } from '@/lib/crm/realtime';
import { createSimpleContext } from '@/lib/hooks/useDataContext';
import { useViewHealthScope } from '@/lib/view-health';
import type { CrmObject } from '@/types/crm';

import { RECORD_RESTRICTED_ENTITY_TYPES } from '../../core/entityTypes';
import type { AssociationChange } from '../buildProjectedObject';
import { UNSAVED_SECTIONS } from '../draftSections';
import { useDraftSaveBarState } from '../hooks/useDraftSaveBarState';
import { useOverlaySubscription } from '../hooks/useOverlaySubscription';
import { useOverviewEditState } from '../hooks/useOverviewEditState';
import { useRecordSlideoutQuery } from '../hooks/useRecordSlideoutQuery';
import { useStoreObjectSubscription } from '../hooks/useStoreObjectSubscription';
import { useMeetingRecordTelemetry } from '../useMeetingRecordTelemetry';
import { RecordDraftProvider } from './RecordDraftContext';
import { type RecordViewObject, RecordViewProvider } from './RecordViewContext';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider and hook co-located */

const EMPTY_PREVIOUS_VALUES: Record<string, unknown> = {};
const EMPTY_ASSOCIATION_CHANGES: Record<string, AssociationChange> = {};
const EMPTY_PROPERTIES: Record<string, unknown> = {};

export type RecordSurfaceState =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'denied'; statusCode: number | undefined }
  | { status: 'ready'; object: RecordViewObject };

export interface RecordSurfaceValue {
  state: RecordSurfaceState;
  /** Spread onto `<UnsavedChangesModal>` by the body region. */
  unsavedChangesModalProps: UnsavedChangesModalProps;
}

const RecordSurface = createSimpleContext<RecordSurfaceValue>('RecordSurface');

/** The surface's resolution state. Throws outside a `RecordSurfaceProvider`. */
export const useRecordSurface = RecordSurface.use;

/**
 * Supplies a FIXED surface value — for stories and tests that want to show a
 * region in a given state without the store, the query, or the network.
 * Production code mounts `RecordSurfaceProvider`.
 */
export const RecordSurfaceStateProvider = RecordSurface.Provider;

/**
 * How a REVIEW host commits the draft this surface is showing: the floating
 * `SaveBar` approves the proposal on Save and rejects it on Cancel.
 */
export interface DraftCommit {
  /**
   * The pending proposal to commit. `undefined` when nothing is proposed —
   * the bar then stays hidden and the surface behaves as a live record.
   */
  proposalId: number | undefined;
  /** Called after a successful approve — e.g. so the host can re-read its source. */
  onApproved?: () => void;
  /** Called after a successful reject (Cancel) — the host needs the same re-read. */
  onRejected?: () => void;
}

export interface RecordSurfaceProviderProps {
  objectId: string;
  /**
   * Receives the unsaved-changes guard while edits are pending and `null`
   * once they clear. Hosts wrap their own dismissal paths (Escape, stack
   * pushes) with it.
   */
  onGuardChange?: (guard: ((action: () => void) => boolean) | null) => void;
  /**
   * Makes this surface a draft REVIEW host: Save approves the proposal
   * instead of writing inline edits. Omit on surfaces that only show a draft.
   */
  draftCommit?: DraftCommit;
  /** Suppress the header's action bar — see `RecordViewData.hideActions`. */
  hideActions?: boolean;
  /** Override the type chip label — see `RecordViewData.typeLabelOverride`. */
  typeLabelOverride?: string;
  children: ReactNode;
}

export function RecordSurfaceProvider({
  objectId,
  onGuardChange,
  draftCommit,
  hideActions,
  typeLabelOverride,
  children,
}: RecordSurfaceProviderProps) {
  const { currentOrg } = useOrg();

  const overlay = useOverlaySubscription(objectId);
  const hasCreateOverlay = overlay?.kind === 'create';

  // Restricted entity types receive no live WS store updates without an
  // explicit subscription — subscribe while any record surface is open so
  // their data stays fresh.
  useRestrictedSubscription(RECORD_RESTRICTED_ENTITY_TYPES);

  // Router/browser-close blocking is off — the surface's own close/back are
  // guarded by the host through `onGuardChange`, and outer navigation while
  // a record is open is already an explicit user action.
  const {
    saveBarState: editSaveBarState,
    setSaveBarState,
    hasUnsavedChanges,
    guard,
    modalProps,
  } = useOverviewEditState({
    blockRouterNavigation: false,
    blockBrowserClose: false,
  });

  // The proposal-backed producer. Returns null without a `proposalId`, so
  // every host that does not pass `draftCommit` is byte-for-byte at the
  // inline-edit behaviour above. Inline editing is itself suppressed while a
  // draft context is active, so the two producers never compete — and a
  // pending proposal persists on its own, so it needs no unsaved-changes
  // guard (the guard stays driven by inline edits only).
  const proposedPropertyNames = useMemo(
    () => Object.keys(overlay?.properties ?? EMPTY_PROPERTIES),
    [overlay]
  );
  // Association edits (a stage move, a company link/unlink) are changes the
  // proposal makes to THIS record too. They must count, or a proposal that is
  // only "move this deal to Proposal" renders the record with the SaveBar
  // hidden — and no way to approve it from here.
  const proposedAssociationCount =
    (overlay?.associations?.length ?? 0) +
    (overlay?.removedAssociations?.length ?? 0);
  const draftSaveBarState = useDraftSaveBarState({
    orgId: currentOrg?.id,
    proposalId: draftCommit?.proposalId,
    changeCount: proposedPropertyNames.length + proposedAssociationCount,
    proposedPropertyNames,
    entityTypeName: overlay?.entityTypeName,
    onApproved: draftCommit?.onApproved,
    onRejected: draftCommit?.onRejected,
  });
  const saveBarState = draftSaveBarState ?? editSaveBarState;

  // Synchronizes the guard with the host (an external registry — the stack
  // context's push guard), which is what effects are for.
  useEffect(() => {
    onGuardChange?.(hasUnsavedChanges ? guard : null);
    return () => {
      onGuardChange?.(null);
    };
  }, [hasUnsavedChanges, guard, onGuardChange]);

  const { storeObjectData, storeObjectIsComplete } =
    useStoreObjectSubscription(objectId);

  const { loading, error, isFetching, statusCode, suppress404 } =
    useRecordSlideoutQuery({
      objectId,
      orgId: currentOrg?.id,
      hasCreateOverlay,
      storeObjectIsComplete,
    });

  // The overlay (associated_objects) lives in the store: queryFn upserts the
  // full object including associated_objects so it persists across WS
  // updates.
  const associatedObjectsOverlay = storeObjectData?.associated_objects;

  // Source of truth for `object`, in order:
  //   1. Access denial (403 always; 404 unless the record is a pending
  //      optimistic creation) → null: never surface a stale store entry the
  //      user no longer has access to / that genuinely doesn't exist.
  //   2. Complete store entry → use it (real-time WS updates flow there).
  //   3. Summary store entry → first-paint identity so the surface doesn't
  //      flash a full skeleton. Suppressed when the fetch has errored
  //      (non-403/404) so the error state shows instead of stale data. The
  //      pending-creation 404 is the deliberate exception: the store entry IS
  //      the record the user just created.
  const object = useMemo<RecordViewObject | null>(() => {
    if (error && (statusCode === 403 || (statusCode === 404 && !suppress404))) {
      return null;
    }
    if (storeObjectData && (storeObjectIsComplete || !error || suppress404)) {
      return {
        ...storeObjectData,
        associated_objects: associatedObjectsOverlay,
      };
    }
    return null;
  }, [
    error,
    statusCode,
    suppress404,
    storeObjectData,
    storeObjectIsComplete,
    associatedObjectsOverlay,
  ]);

  const projection = useMemo(() => {
    if (!overlay) {
      return null;
    }
    if (overlay.kind === 'create') {
      return projectOverlay(undefined, overlay, currentOrg?.id ?? 0) ?? null;
    }
    // update: project only once the real record has loaded — never flash a
    // synthetic partial over a record that is still fetching.
    if (!object) {
      return null;
    }
    return projectOverlay(object, overlay, currentOrg?.id ?? 0) ?? null;
  }, [overlay, object, currentOrg?.id]);

  const renderObject: RecordViewObject | null =
    (projection?.projectedObject as RecordViewObject | undefined) ?? object;
  const sections = hasCreateOverlay ? UNSAVED_SECTIONS : undefined;

  const state = useMemo<RecordSurfaceState>(() => {
    if (renderObject) {
      return { status: 'ready', object: renderObject };
    }
    if (loading || isFetching) {
      return { status: 'loading' };
    }
    if (error && statusCode !== 404 && statusCode !== 403) {
      return { status: 'error', error };
    }
    if (currentOrg) {
      return { status: 'denied', statusCode };
    }
    return { status: 'loading' };
  }, [renderObject, loading, isFetching, error, statusCode, currentOrg]);

  // The surface's own observability scope — deliberately here, not in the
  // body, whose job is rendering, not reporting on this surface.
  const successVariant = useMeetingRecordTelemetry(object as CrmObject | null);
  useViewHealthScope('overlay', 'crm-object-slideout', {
    // Only start the scope once the record is resolved — prevents an
    // eviction-race refetch (isFetching=true, object=null) from being
    // recorded as a healthy view.
    enabled: Boolean(object),
    entityType: object?.entity_type_name,
    successVariant,
  });

  const value = useMemo<RecordSurfaceValue>(
    () => ({ state, unsavedChangesModalProps: modalProps }),
    [state, modalProps]
  );

  return (
    <RecordSurface.Provider value={value}>
      {state.status === 'ready' ? (
        <RecordDraftProvider
          active={Boolean(projection)}
          previousValues={projection?.previousValues ?? EMPTY_PREVIOUS_VALUES}
          associationChanges={
            projection?.associationChanges ?? EMPTY_ASSOCIATION_CHANGES
          }
          isUnsaved={hasCreateOverlay}
          proposalId={draftCommit?.proposalId}
        >
          <RecordViewProvider
            object={state.object}
            sections={sections}
            hideActions={hideActions}
            typeLabelOverride={typeLabelOverride}
            saveBarState={saveBarState}
            onSaveStateChange={setSaveBarState}
          >
            {children}
          </RecordViewProvider>
        </RecordDraftProvider>
      ) : (
        children
      )}
    </RecordSurface.Provider>
  );
}
